import React, { useEffect, useRef, useState } from "react";

import LogoBadge from "@svg/logo-badge";

import AppModals from "@app/AppModals";
import { Button } from "@replo/design-system/components/button/Button";
import { Empty } from "@replo/design-system/components/empty/Empty";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { skipToken } from "@tanstack/react-query";
import { MobilePreview } from "@v2/components/MobilePreview";
import { analytics } from "@v2/features/infra/analytics/tracking";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { useDocumentTitle } from "@v2/hooks/useDocumentTitle";
import { useModal } from "@v2/store/modalStore";
import { fileTemplateRouter } from "@v2/trpc/routes/fileTemplate";
import { routes } from "@v2/utils/router";
import { ArrowRight, FileQuestion, MonitorSmartphone } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

const SharePageTemplateContent = () => {
  const navigate = useNavigate();
  const { openModal } = useModal();
  const [viewport, setViewport] = useState<"mobile" | "desktop">("desktop");
  const currentUser = useCurrentUser();

  const { templateId } = useParams<{
    templateId: string;
  }>();

  const shouldReopenModal =
    sessionStorage.getItem("replo_reopen_template_modal") === "true" &&
    templateId;

  useEffect(() => {
    if (shouldReopenModal) {
      sessionStorage.removeItem("replo_reopen_template_modal");
      openModal({
        type: "selectProjectForTemplateModal",
        props: { pageTemplateId: templateId },
      });
    }
  }, [shouldReopenModal, templateId, openModal]);

  const { data: template, isLoading: isLoadingTemplate } =
    fileTemplateRouter.getPublic.useQuery(
      templateId ? { id: templateId } : skipToken,
    );

  const shouldFetchAffiliateLink =
    templateId && !currentUser.isAuthenticated && !currentUser.isLoading;
  const { data: affiliateLinkData } =
    fileTemplateRouter.getOwnerAffiliateLink.useQuery(
      shouldFetchAffiliateLink ? { id: templateId } : skipToken,
    );

  const hasTrackedViewRef = useRef(false);
  useEffect(() => {
    if (
      template &&
      templateId &&
      !currentUser.isLoading &&
      !hasTrackedViewRef.current
    ) {
      hasTrackedViewRef.current = true;
      analytics.logEvent("sharedPageTemplate.viewed", {
        templateId,
        templateName: template.name,
        userId: currentUser.user?.id ?? null,
      });
    }
  }, [template, templateId, currentUser.isLoading, currentUser.user?.id]);

  useDocumentTitle(template?.name ?? null);

  const handleUseTemplate = () => {
    if (!currentUser.isAuthenticated && !currentUser.isLoading) {
      if (affiliateLinkData?.affiliateLink) {
        // Note (Ezra, 2025-12-11): Must use window.location.href for affiliate links
        // because they're on a different origin (try.replo.app). React Router's
        // navigate() only works for same-origin routes.
        const url = new URL(affiliateLinkData.affiliateLink);
        url.searchParams.set("pageTemplateId", templateId ?? "");
        window.location.href = url.toString();
      } else {
        const searchParams = new URLSearchParams();
        searchParams.set("pageTemplateId", templateId ?? "");
        navigate(`${routes.signup}?${searchParams.toString()}`);
      }
      return;
    }

    if (templateId) {
      openModal({
        type: "selectProjectForTemplateModal",
        props: {
          pageTemplateId: templateId,
        },
      });
    }
  };

  const handleViewportToggle = () => {
    setViewport(viewport === "mobile" ? "desktop" : "mobile");
  };

  const tooltipText = (() => {
    const nextViewport = viewport === "mobile" ? "desktop" : "mobile";
    const nextWidth = nextViewport === "mobile" ? "375" : "100%";
    return `Switch to ${nextViewport} (${nextWidth}${nextViewport === "mobile" ? "px" : ""})`;
  })();

  return (
    <div className="relative flex h-screen w-full bg-sheet">
      <div className="flex-1 flex items-center justify-center bg-sheet">
        {isLoadingTemplate && <Skeleton className="w-full h-full" />}
        {!isLoadingTemplate && template && template.staticHtml && (
          <div className="flex items-center justify-center bg-sheet w-full h-full">
            {viewport === "mobile" ? (
              <div
                key="mobile-view"
                className="animate-in fade-in duration-300 w-full h-[85vh] flex items-center justify-center"
              >
                <MobilePreview deviceColor="black">
                  <iframe
                    srcDoc={template.staticHtml}
                    className="no-scrollbar w-full h-full bg-white"
                    sandbox="allow-scripts allow-same-origin"
                    title="Template Preview"
                  />
                </MobilePreview>
              </div>
            ) : (
              <div
                key="desktop-view"
                className="h-full w-full animate-in fade-in duration-300"
              >
                <iframe
                  srcDoc={template.staticHtml}
                  className="w-full h-full bg-white"
                  sandbox="allow-scripts allow-same-origin"
                  title="Template Preview"
                />
              </div>
            )}
          </div>
        )}
        {!isLoadingTemplate && (!template || !template.staticHtml) && (
          <Empty
            title="Page Not Found"
            description="The page you're looking for could not be found."
            icon={<FileQuestion />}
            UNSAFE_className="bg-sheet"
          >
            <Button
              variant="tertiary"
              size="base"
              onClick={() => navigate("/")}
            >
              Go Home
            </Button>
          </Empty>
        )}
      </div>

      {!isLoadingTemplate && template && viewport === "desktop" && (
        <div className="fixed bottom-0 left-0 right-0 h-40 pointer-events-none">
          <div
            className="absolute inset-0 backdrop-blur-md"
            style={{
              maskImage:
                "linear-gradient(to top, black 0%, black 85%, transparent 100%)",
              WebkitMaskImage:
                "linear-gradient(to top, black 0%, black 85%, transparent 100%)",
            }}
          />
        </div>
      )}

      {!isLoadingTemplate && template && (
        <div className="fixed bottom-0 right-0">
          <div className="relative flex flex-col gap-2 w-fit mx-6 mt-6 mb-6">
            <div className="flex gap-2 w-full">
              <Tooltip content={tooltipText} triggerAsChild>
                <Button
                  icon
                  variant="secondary"
                  size="lg"
                  onClick={handleViewportToggle}
                >
                  <MonitorSmartphone />
                </Button>
              </Tooltip>
              <Button
                variant="secondary"
                size="lg"
                onClick={handleUseTemplate}
                className="flex-1"
                endEnhancer={<ArrowRight size={20} />}
              >
                Use Page
              </Button>
            </div>

            <Button
              variant="tertiary"
              size="lg"
              onClick={() => window.open("https://replo.app", "_blank")}
              startEnhancer={
                <LogoBadge className="h-5 w-5 text-brand-seashell" />
              }
              className="w-full"
              UNSAFE_className="bg-black text-brand-seashell shadow-lg hover:bg-gray-900 px-4 py-2 gap-2"
            >
              Made in Replo
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const SharePageTemplateLayout = () => {
  return (
    <>
      <AppModals />
      <SharePageTemplateContent />
    </>
  );
};

export default SharePageTemplateLayout;
